const SERVICE_WORKER_URL = '/service-worker.js';
const UPDATE_CHECK_MIN_INTERVAL_MS = 5 * 60 * 1000;

export function registerServiceWorker({ onUpdate } = {}) {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production') return;

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL);
      let lastUpdateCheckAt = 0;

      const notifyIfWaiting = () => {
        if (registration.waiting && navigator.serviceWorker.controller) {
          onUpdate?.(registration);
          return true;
        }
        return false;
      };

      const checkForUpdate = ({ force = false } = {}) => {
        if (notifyIfWaiting()) return;
        if (typeof registration.update !== 'function') return;
        const now = Date.now();
        if (!force && now - lastUpdateCheckAt < UPDATE_CHECK_MIN_INTERVAL_MS) return;
        lastUpdateCheckAt = now;
        registration.update().then(notifyIfWaiting).catch(() => {});
      };

      notifyIfWaiting();

      registration.addEventListener('updatefound', () => {
        const worker = registration.installing;
        if (!worker) return;

        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) {
            onUpdate?.(registration);
          }
        });
      });

      checkForUpdate({ force: true });

      const handleFocus = () => checkForUpdate();
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      };
      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Service worker registration failed', err);
      }
    }
  });
}

export async function unregisterExistingServiceWorkers() {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Service worker cleanup failed', err);
    }
  }
}

export function applyServiceWorkerUpdate(registration) {
  const waitingWorker = registration?.waiting;
  if (!waitingWorker || typeof waitingWorker.postMessage !== 'function') return false;
  try {
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    return true;
  } catch (err) {
    return false;
  }
}
